import type { UpdateTournamentAvailabilitySchema } from "@teamz/common";
import type { ComponentProps } from "react";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import TournamentIcon from "@/assets/tournaments.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { playerMatchAvailabilities, updateTournamentAvailabilitySchema } from "@teamz/common";
import { cva, type VariantProps } from "class-variance-authority";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useUserRole } from "@/hooks/use-user-role";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { PremiumBadge } from "../premium-badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField } from "../ui/text-field";

const emptyButtonVariants = cva(
  "inline-flex h-12 w-full items-center justify-between rounded-xl bg-neutral-50 p-1.5 text-neutral-600",
  {
    variants: {
      variant: {
        "non-available": ["border border-neutral-400 text-neutral-450"],
        "non-confirmed": ["border border-dashed border-neutral-450"],
        "premium": ["border border-neutral-450"],
      },
    },
    defaultVariants: {
      variant: "non-confirmed",
    },
  },
);
type EmptyButtonProps = ComponentProps<"button"> & VariantProps<typeof emptyButtonVariants> & {
  category: string;
  status?: string;
  userRole?: string;
};

function EmptyButton({ category, status, variant, userRole, className, ...props }: EmptyButtonProps) {
  const { t } = useTranslation(["profile"]);
  return (
    <button
      type="button"
      className={cn(
        emptyButtonVariants({ variant }),
        userRole === "club" && variant !== "premium" && "pointer-events-none border-solid",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-1 px-2">
        <TournamentIcon />
        <span className="text-sm font-medium">{category}</span>
      </div>

      { variant === "premium"
        ? (
            <PremiumBadge />
          )
        : (
            <div className="flex items-center gap-2 px-2">
              <span className={cn(
                "text-sm font-medium",
                variant === "non-confirmed" ? "text-orange-500" : "text-neutral-450",
                (userRole === "club" && variant === "non-confirmed") && "text-neutral-500",
                (userRole === "club" && variant === "non-available") && "text-neutral-450",
              )}
              >
                { userRole !== "club" && status}
                {
                  userRole === "club" && (
                    <>
                      {variant === "non-confirmed" && t("profile:not-confirmed")}
                      {variant === "non-available" && t("profile:not-available")}
                    </>
                  )
                }
              </span>
              {
                userRole !== "club" && (
                  <>
                    {variant === "non-confirmed" && <AddIcon className="size-6 text-orange-500" />}
                    {variant === "non-available" && <EditIcon className="size-5 text-neutral-500" />}
                  </>
                )
              }
            </div>
          )}
    </button>
  );
}

function TournamentButton({ player, userRole, className, ...props }: ComponentProps<"button"> & { player: RouterOutputs["player"]["getPlayer"]; userRole?: string }) {
  const { t } = useTranslation(["profile", "common"]);

  return (
    <button
      type="button"
      disabled={userRole === "club"}
      className={cn("flex h-12 w-full items-center justify-between rounded-xl border p-1.5", className)}
      {...props}
    >
      <div className="flex items-center gap-1 px-2 text-neutral-900">
        <TournamentIcon />
        <span className="text-sm font-medium">{t("common:desktop-nav.tournaments")}</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-orange-500">{t("profile:available")}</span>
        { userRole !== "club" && <EditIcon className="size-5 text-neutral-500" />}
      </div>
    </button>
  );
}

function PlayerTournamentForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateTournamentAvailability.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerTournamentForm = useForm<UpdateTournamentAvailabilitySchema>({
    resolver: zodResolver(updateTournamentAvailabilitySchema),
    defaultValues: {
      tournamentAvailabilities: player?.tournamentAvailabilities || undefined,
    },
  });

  const onSubmit = playerTournamentForm.handleSubmit((data) => {
    mutate({
      userId: player.userId,
      tournamentAvailabilities: data.tournamentAvailabilities,
    });
  });

  return (
    <>
      <SheetForm id="PlayerTournamentForm" onSubmit={onSubmit}>
        <Controller
          control={playerTournamentForm.control}
          name="tournamentAvailabilities"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>
                {t("profile:availability")}
              </Label>
              <Select
                value={field.value || undefined}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerMatchAvailabilities.map(availability => (
                      <SelectItem key={availability} value={availability}>
                        {t(`profile:${availability}`)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerTournamentForm" loading={isPending || playerTournamentForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function PlayerTournament({ player }: { player: RouterOutputs["player"]["getPlayer"] }) {
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["profile", "common"]);
  const userRole = useUserRole();
  const { user } = useMe();
  const navigate = useNavigate();

  return (
    <>
      {
        !user.isAdmin && user.plan === "pack_equipe" && (
          <EmptyButton
            onClick={() => navigate("/plans")}
            variant="premium"
            category={t("common:desktop-nav.tournaments")}
            status={t("profile:not-available")}
            userRole={userRole}
          />
        )
      }

      { !player?.tournamentAvailabilities && user.plan !== "pack_equipe" && (
        <EmptyButton
          onClick={() => updateModalState("open")}
          variant="non-confirmed"
          category={t("common:desktop-nav.tournaments")}
          status={t("profile:complete")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      ) }

      {player?.tournamentAvailabilities === "not-available" && user.plan !== "pack_equipe"
      && (
        <EmptyButton
          onClick={() => updateModalState("open")}
          variant="non-available"
          category={t("common:desktop-nav.tournaments")}
          status={t("profile:not-available")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      )}

      {player?.tournamentAvailabilities === "available" && user.plan !== "pack_equipe"
      && (
        <TournamentButton
          player={player}
          onClick={() => updateModalState("open")}
          userRole={user.isAdmin ? "player" : userRole}
        />
      )}

      { (userRole !== "club" || user.isAdmin) && (
        <Sheet open={modalState === "open"} onOpenChange={() => updateModalState("close")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {t("profile:availability")}
              </SheetTitle>
            </SheetHeader>

            <PlayerTournamentForm player={player} closeModal={() => updateModalState("close")} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
